import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import cx from 'classnames';

import {useTranslation} from 'react-i18next';

import {useIsInWindowBottomHalf, useIsInWindowRightHalf} from '@pexip/hooks';
import type {TooltipPosition} from '@pexip/components';

import type {
    InMeetingDraggablePosition,
    InMeetingDraggableProps,
} from '../../InMeetingDraggable/InMeetingDraggable.view';
import {InMeetingDraggable} from '../../InMeetingDraggable/InMeetingDraggable.view';
import type {FoldedSelfviewProps} from '../FoldedSelfview/FoldedSelfview.view';
import {FoldedSelfview} from '../FoldedSelfview/FoldedSelfview.view';
import type {InMeetingSelfviewProps} from '../InMeetingSelfview/InMeetingSelfview.view';
import {InMeetingSelfview} from '../InMeetingSelfview/InMeetingSelfview.view';
import {useDoubleTap} from '../../../hooks/useDoubleTap';
import {getLangDirection} from '../../../utils/getLangDirection';

import styles from './DraggableFoldableInMeetingSelfview.module.scss';

export const DraggableFoldableInMeetingSelfview: React.FC<
    InMeetingSelfviewProps &
        FoldedSelfviewProps &
        Pick<
            InMeetingDraggableProps,
            | 'draggableAriaLabel'
            | 'floatRoot'
            | 'isSidePanelVisible'
            | 'onMouseEnter'
            | 'onMouseLeave'
            | 'onFocus'
            | 'onBlur'
        > & {
            isFolded: boolean;
            expandSelfView: boolean;
            effectsManagerView?: React.ReactElement | null;
            testId?: string;
        }
> = ({
    isFolded,
    draggableAriaLabel,
    testId,
    floatRoot,
    isSidePanelVisible,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    expandSelfView,
    ...props
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const alignWithFloatRoot = useRef<(() => void) | null>(null);

    const {isInBottomHalf, update: updateIsInBottomHalf} =
        useIsInWindowBottomHalf();
    const {isInRightHalf, update: updateIsInRightHalf} =
        useIsInWindowRightHalf();

    const [isSelfviewMaximized, setIsSelfviewMaximized] = useState(false);
    const onDoubleTap = useDoubleTap(e => {
        e.stopPropagation();
        setIsSelfviewMaximized(current => !current);
    });

    const {i18n} = useTranslation();
    const dir = getLangDirection(i18n);

    const onPositionChangeCb = useCallback(
        (el: HTMLDivElement, position: InMeetingDraggablePosition) => {
            updateIsInBottomHalf(el);
            updateIsInRightHalf(el);
            if (dir === 'ltr' && position.floatRootOverflow.right) {
                el.style.removeProperty('left');
            }
        },
        [updateIsInBottomHalf, updateIsInRightHalf, dir],
    );

    useEffect(() => {
        if (ref.current) {
            updateIsInRightHalf(ref.current);
        }
    }, [updateIsInRightHalf]);

    useEffect(() => {
        if (ref.current) {
            updateIsInBottomHalf(ref.current);
        }
    }, [updateIsInBottomHalf]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Intended
    useLayoutEffect(() => {
        // using layout effect here because we are fiddling with the element's dimensions
        // https://kentcdodds.com/blog/useeffect-vs-uselayouteffect
        if (ref.current && floatRoot?.current && alignWithFloatRoot.current) {
            alignWithFloatRoot.current();
        }
    }, [floatRoot, isFolded]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Intended
    useLayoutEffect(() => {
        ref.current?.style.removeProperty('top');
        ref.current?.style.removeProperty('bottom');
        ref.current?.style.removeProperty('left');
        ref.current?.style.removeProperty('right');
        ref.current?.style.removeProperty('transform');
        ref.current?.style.removeProperty('translate');
    }, [isSelfviewMaximized]);

    const calculateOrientation = () => {
        // If in bottom half
        if (isInBottomHalf) {
            // If in right half
            if (isInRightHalf) {
                return dir === 'ltr' ? 'topEnd' : 'topStart';
            }
            return dir === 'ltr' ? 'topStart' : 'topEnd';
        }
        // If in top half
        if (isInRightHalf) {
            return dir === 'ltr' ? 'bottomEnd' : 'bottomStart';
        }
        return dir === 'ltr' ? 'bottomStart' : 'bottomEnd';
    };

    const isDisplayingEffectsManagerOnDesktop =
        !!props.effectsManagerView && !props.isMobileDevice;

    const getClassNames = useCallback(
        () =>
            isFolded
                ? {[styles.foldedSelfviewWrapper]: true}
                : {
                      [styles.pip]: true,
                      [styles.maximized]: isSelfviewMaximized,
                      [styles.effectsManagerDesktopPip]:
                          isDisplayingEffectsManagerOnDesktop,
                  },
        [isFolded, isSelfviewMaximized, isDisplayingEffectsManagerOnDesktop],
    );

    const notificationTooltipPos: TooltipPosition = calculateOrientation();
    const selfviewBtnTooltipPos: TooltipPosition = calculateOrientation();

    const {
        isAudioInputMuted,
        isAudioInputMissing,
        isAudioInputDisabled,
        isVideoInputMuted,
        isVideoInputMissing,
        onToggleVideoClick,
        onExpandSelfview,
        onToggleAudioClick,
        showSelfviewTooltip,
        setShowSelfviewTooltip,
        ...inMeetingSelfviewProps
    } = props;

    const foldedSelfviewProps = {
        isAudioInputDisabled,
        isAudioInputMuted,
        isAudioInputMissing,
        isVideoInputMuted,
        isVideoInputMissing,
        onToggleVideoClick,
        onExpandSelfview,
        onToggleAudioClick,
        showSelfviewTooltip,
        setShowSelfviewTooltip,
    };

    return (
        <>
            {isSelfviewMaximized && (
                <div
                    className={styles.maximizedMask}
                    onPointerUp={e => e.stopPropagation()}
                />
            )}
            <InMeetingDraggable
                isDisabled={isSelfviewMaximized}
                draggableAriaLabel={draggableAriaLabel}
                testId={testId}
                isMoveableWithArrowKeys
                tabIndex={isFolded ? -1 : 0}
                showFocus={false}
                floatRoot={floatRoot}
                isSidePanelVisible={isSidePanelVisible}
                isPhone={props.isMobileDevice}
                onRepositionSideEffect={onPositionChangeCb}
                shouldCaptureClick={false}
                className={cx(
                    styles.inMeetingSelfviewPosition,
                    getClassNames(),
                )}
                ref={ref}
                alignWithFloatRootTrigger={alignWithFloatRoot}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onFocus={onFocus}
                onBlur={onBlur}
            >
                {isFolded && (
                    <FoldedSelfview
                        {...foldedSelfviewProps}
                        notificationTooltipPos={notificationTooltipPos}
                        selfviewBtnTooltipPos={selfviewBtnTooltipPos}
                        expandSelfView={expandSelfView}
                        isMobileDevice={props.isMobileDevice}
                    />
                )}
                <InMeetingSelfview
                    isAudioInputMuted={isAudioInputMuted}
                    isAudioInputDisabled={isAudioInputDisabled}
                    isVideoInputMuted={isVideoInputMuted}
                    // Workaround a bug where Video element being frozen by
                    // adding/removing from the DOM. In stead of conditional
                    // rendering the Video element, use CSS to hide and show to
                    // keep the Video element in the DOM
                    aspectRatioWrapperClassName={isFolded ? 'd-none' : ''}
                    {...inMeetingSelfviewProps}
                    onPointerDownCapture={
                        props.isMobileDevice
                            ? (e: React.PointerEvent<HTMLDivElement>) => {
                                  if (isSelfviewMaximized) {
                                      e.stopPropagation();
                                  }
                                  onDoubleTap(e);
                              }
                            : undefined
                    }
                    isMaximized={isSelfviewMaximized}
                    setIsMaximized={setIsSelfviewMaximized}
                />
            </InMeetingDraggable>
        </>
    );
};

export type DraggableFoldableInMeetingSelfviewProps = React.ComponentProps<
    typeof DraggableFoldableInMeetingSelfview
>;
